import { Component } from "@theme/component";

export class TooltipTriggerComponent extends Component {
  static instances = new Map();

  /**
   * @param {string} id
   */
  static getOrCreateState(id) {
    if (!TooltipTriggerComponent.instances.has(id)) {
      TooltipTriggerComponent.instances.set(id, {
        open: false,
        activeTrigger: null,
        triggers: new Set(),
        container: null
      });
    }
    return TooltipTriggerComponent.instances.get(id);
  }

  connectedCallback() {
    super.connectedCallback();

    this.tooltipId = this.getAttribute("tooltip-id");
    if (!this.tooltipId) {
      console.warn("tooltip-trigger: missing tooltip-id attribute");
      return;
    }

    this.state = TooltipTriggerComponent.getOrCreateState(this.tooltipId);
    this.state.triggers.add(this);

    // Find the container if not already found
    if (!this.state.container) {
      this.state.container = document.querySelector(`[data-tooltip-container="${this.tooltipId}"]`);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.state) {
      this.state.triggers.delete(this);

      // Clean up global listeners if this was the active trigger
      if (this.state.activeTrigger === this) {
        this.close();
      }

      // Clean up registry if no more triggers
      if (this.state.triggers.size === 0) {
        TooltipTriggerComponent.instances.delete(this.tooltipId);
      }
    }
  }

  toggle(e) {
    if (e) e.stopPropagation();

    if (this.state.open) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    const { container } = this.state;
    if (!container) {
      console.warn(`tooltip-trigger: container not found for id "${this.tooltipId}"`);
      return;
    }

    // Close any other open tooltips
    TooltipTriggerComponent.instances.forEach((state, id) => {
      if (id !== this.tooltipId && state.open) {
        state.triggers.forEach((trigger) => trigger.close());
      }
    });

    this.state.open = true;
    this.state.activeTrigger = this;

    // Add open classes
    this.state.triggers.forEach((trigger) => trigger.classList.add("tooltip-open"));
    container.classList.add("tooltip-open");
    document.body.classList.add("tooltip-open");

    const closestProductCard = container.closest(".product-item");
    if (closestProductCard) {
      closestProductCard.classList.add("product-item-tooltip-open");
    }

    // Position and add global listeners
    this.positionTooltip();
    document.body.addEventListener("click", this.onBodyClickBound);
    window.addEventListener("scroll", this.onWindowScrollBound);
  }

  close() {
    const { container } = this.state;
    if (!container) return;

    this.state.open = false;
    this.state.activeTrigger = null;

    // Remove open classes
    this.state.triggers.forEach((trigger) => trigger.classList.remove("tooltip-open"));
    container.classList.remove("tooltip-open");
    document.body.classList.remove("tooltip-open");

    const closestProductCard = container.closest(".product-item");
    if (closestProductCard) {
      closestProductCard.classList.remove("product-item-tooltip-open");
    }

    // Remove global listeners
    document.body.removeEventListener("click", this.onBodyClickBound);
    window.removeEventListener("scroll", this.onWindowScrollBound);
  }

  onBodyClickBound = (e) => {
    const { container } = this.state;

    // Check if click is on any trigger for this tooltip
    const clickedOnTrigger = Array.from(this.state.triggers).some(
      (trigger) => trigger === e.target || trigger.contains(e.target)
    );
    if (clickedOnTrigger) return;

    // Check if click is inside the container
    if (container && container.contains(e.target)) return;

    this.close();
  };

  onWindowScrollBound = () => {
    this.close();
  };

  positionTooltip() {
    const { container, activeTrigger } = this.state;
    if (!container || !activeTrigger) return;

    const triggerRect = activeTrigger.getBoundingClientRect();

    container.style.left = "0px";
    container.style.top = "0px";

    const containerRect = container.getBoundingClientRect();
    const gap = 8;
    const padding = 12;
    const isMobile = window.innerWidth < 750;

    let top, left;

    if (isMobile) {
      // On mobile, center horizontally and place below the trigger by default
      const maxLeft = window.innerWidth - containerRect.width - padding;
      left = Math.max(
        padding,
        Math.min(triggerRect.left + triggerRect.width / 2 - containerRect.width / 2, maxLeft)
      );

      const belowTop = triggerRect.bottom + gap;
      const aboveTop = triggerRect.top - containerRect.height - gap;
      
      // Fallback to above if below doesn't fit
      if (belowTop + containerRect.height <= window.innerHeight - padding) {
        top = belowTop;
      } else {
        const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue("--header-height") || "0");
        top = Math.max(padding + headerHeight, aboveTop);
      }
    } else {
      // On desktop, align top with trigger and place to the right by default
      const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue("--header-height") || "0");
      const minTop = Math.max(padding, headerHeight + padding);

      const rightOfTrigger = triggerRect.right + gap;

      left = rightOfTrigger;
      top = Math.max(minTop, triggerRect.top);

      // If it doesn't fit on the right, render below it
      if (left + containerRect.width > window.innerWidth - padding) {
        top = triggerRect.bottom + gap;
        
        const maxLeft = window.innerWidth - containerRect.width - padding;
        left = Math.max(
          padding,
          Math.min(triggerRect.left + triggerRect.width / 2 - containerRect.width / 2, maxLeft)
        );
      } else {
        // Ensure it doesn't overflow the bottom of the window
        const maxTop = window.innerHeight - containerRect.height - padding;
        top = Math.min(top, maxTop);
      }
    }

    container.style.left = `${left}px`;
    container.style.top = `${top}px`;
  }
}

if (!customElements.get("tooltip-trigger")) {
  customElements.define("tooltip-trigger", TooltipTriggerComponent);
}
